import textStyles from '@/styles/atoms/Text/styles.module.css'
import BaseText from '@emcasa/ui-dom/components/Text'
import styled from 'styled-components'
import slugify from 'slugify'
import BaseButton from '@/components/shared/Button/Link'
import labelStyles from '@/styles/atoms/Label/styles.module.css'
import Row from '@emcasa/ui-dom/components/Row'
import {useState, useMemo} from 'react'
import {getAddressInput} from '@/lib/address'
import {price} from '@/lib/format'
import {onInputFocus} from '@/lib/inputFocus'
import CurrencyInput from '@/components/form/CurrencyInput'
import logger from '@/lib/logger'
import PropTypes from 'prop-types'
import {calcPercentage, MINPERCENTAGE, MAXPERCENTAGE} from '../../lib'
import {MIN_PRICE, FORMATED_MIN_PRICE} from '@/lib/values'

const Card = styled(Row)`
  flex-direction: column;
  cursor: pointer;
  border: ${(props) =>
    props.isSelected ? '1px solid #ff2b7f' : '1px solid #e0e6ed'};
  box-sizing: border-box;
  height: 80px;
  width: 100%;
  border-radius: 8px;
  padding: 10px 20px;
  margin: 10px 10px;
`

const Header = styled(Row)`
  width: 100%;
  justify-content: center;
  flex-wrap: wrap;
  @media (min-width: 600px) {
    justify-content: space-between;
    flex-wrap: nowrap;
  }
`
const Container = styled(Row)`
  align-self: center;
  min-width: 360px;
  @media (min-width: 770px) {
    width: calc(100vw - 30%);
  }
`

const BottomDiv = styled(Row)`
  flex-direction: column;
  @media (min-width: 1024px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`

const Button = styled(BaseButton)`
  width: 100%;
  @media (min-width: 1024px) {
    width: ${(props) => props.width};
  }
`

const Text = styled(BaseText)`
  font: ${(props) =>
    (props.style ? props.style : 'normal') +
    ' ' +
    (props.weight ? props.weight : 'normal') +
    ' ' +
    (props.size ? props.size : '12px') +
    ' ' +
    (props.font ? 'Montserrat' : 'Rubik')};
  line-height: 140%;
  color: ${(props) => (props.color ? props.color : '#1d1919')};
  letter-spacing: ${(props) => props.letterSpacing};
`

const Evaluation = ({resultSetup}) => {
  const [customInput, setCustomInput] = useState(null)

  const {MIN, SIMILARMIN} = MINPERCENTAGE
  const {MAX, SIMILARMAX} = MAXPERCENTAGE
  const cards = {
    default: {
      title: 'Avaliação EmCasa',
      label: 'avaliador',
      price: resultSetup.estimatedPrice,
      text:
        'Anuncie com o valor da nossa avaliação para ter um anúncio competitivo'
    },
    min: {
      title: 'Preço Competitivo',
      label: 'abaixo',
      price: calcPercentage(resultSetup.estimatedPrice, MIN),
      text:
        'Atraia mais pessoas ao seu anúncio com um valor abaixo da nossa avaliação'
    },
    max: {
      title: 'Quero Negociar',
      label: 'acima',
      price: calcPercentage(resultSetup.estimatedPrice, MAX),
      text:
        'Adicione uma margem de negociação ao valor de anúncio do seu imóvel'
    },
    custom: {
      title: 'Personalizado',
      label: 'custom',
      price: customInput,
      text: 'Já tem um valor em mente? Coloque o seu valor no anúncio'
    }
  }

  const [selected, setSelected] = useState({
    type: 'Avaliação EmCasa',
    value: cards['default']['price']
  })

  const idealForYouUrl = useMemo(() => {
    if (resultSetup?.listing?.addressData) {
      const {
        listing: {addressData}
      } = resultSetup
      const address = getAddressInput({addressData})
      return `/imoveis/${slugify(address.state).toLowerCase()}/${slugify(
        address.city
      ).toLowerCase()}/ideal-para-voce`
    }

    return '/imoveis/sp/sao-paulo/ideal-para-voce'
  }, [resultSetup?.listing?.addressData])

  return (
    <>
      {resultSetup.success && (
        <Container>
          <Row margin="20px" flexDirection="column" alignItems="center">
            <Header>
              <Row>
                <img
                  src="/img/illustrations/ilustra-precificador.svg"
                  alt="Avaliar Imóvel"
                />
              </Row>
              <Row
                justifyContent="space-around"
                flexDirection="column"
                alignItems="center"
                margin="20px 0px"
              >
                <Text weight="500" size="16px">
                  Avaliação do Imóvel
                </Text>

                <Text size="36px" weight="600" letterSpacing="-0.5px">
                  {price(resultSetup.estimatedPrice)
                    ? price(resultSetup.estimatedPrice)
                    : 'R$ -'}
                </Text>
                <Row width="277px">
                  <Text
                    size="14px"
                    className={textStyles.ecText}
                    textAlign="center"
                  >
                    {resultSetup.listing.address}
                  </Text>
                </Row>
              </Row>
            </Header>
            <Row
              margin="15px 0px"
              px={4}
              py={5}
              backgroundColor={'#fad7e5'}
              style={{borderRadius: '4px'}}
              flexDirection="column"
              minWidth="94%"
            >
              <Text size="14px" weight="500" className={textStyles.ecText}>
                Como avaliamos o seu imóvel?
              </Text>
              <Text>
                Nossa avaliação considera as principais características do
                imóvel, sua localização e os preços de outras transações
                similares do mercado.
              </Text>
            </Row>
            <Row flexDirection="column" marginTop="10px">
              <Row flexDirection="column" padding="10px">
                <Text size="24px" weight="600" letterSpacing="0.15px">
                  Vender Imóvel
                </Text>
                <Text size="14px">
                  Antes de começar a vender, precisamos ajustar o valor para o
                  seu anúncio. Separamos alguns valores, caso queira usar como
                  referência
                </Text>
              </Row>
              <Row
                flex-direction="row"
                flexWrap="wrap"
                justifyContent="space-between"
              >
                {Object.keys(cards).map((key, index) => (
                  <Card
                    key={index}
                    isSelected={cards[key]['title'] === selected.type}
                    onClick={() => {
                      setSelected({
                        type: cards[key]['title'],
                        value: cards[key]['price']
                      })
                      logger.action('selller-evaluation-price-selected', {
                        type: cards[key]['label']
                      })
                    }}
                  >
                    <Row marginBottom="10px" justifyContent="space-between">
                      <Text weight="500" size="14px">
                        {cards[key]['title']}
                      </Text>
                      <Text color="#FF2B7F" weight="500" size="14px">
                        {cards[key]['title'] !== 'Personalizado'
                          ? price(cards[key]['price'])
                          : customInput
                          ? price(customInput)
                          : 'R$ -'}
                      </Text>
                    </Row>
                    <Text>{cards[key]['text']}</Text>
                  </Card>
                ))}
              </Row>
              <Row margin="20px 0px" flexDirection="column">
                <Text className={textStyles.ecText} weight="500">
                  Valor do seu anúncio:
                </Text>
                <Row margin="20px 0px" flexDirection="column">
                  <CurrencyInput
                    disabled={selected.type !== 'Personalizado'}
                    mutableValue
                    placeholder={'Ex: R$ 400.000'}
                    name="price"
                    tabIndex="0"
                    value={selected.value}
                    onChange={(e) => {
                      selected.type === 'Personalizado'
                        ? (setCustomInput(e),
                          setSelected({...selected, value: e}))
                        : (setCustomInput(null),
                          setSelected({...selected, value: e}))
                    }}
                    onFocus={(e) => {
                      onInputFocus(e.target.offsetTop)
                      if (
                        selected.type === 'Personalizado' &&
                        !selected.value
                      ) {
                        logger.action('seller-evaluation-price-input')
                      }
                    }}
                  />
                  {selected.type === 'Personalizado' &&
                    selected.value < MIN_PRICE && (
                      <span className={labelStyles.ecLabel__warning}>
                        {`O valor mínimo é de ${FORMATED_MIN_PRICE}`}
                      </span>
                    )}
                </Row>
              </Row>
              <Row alignItems="center" flexDirection="column">
                <Button
                  width="171px"
                  active={
                    selected.type !== 'Personalizado' ||
                    (selected.type === 'Personalizado' &&
                      !!selected.value &&
                      selected.value >= MIN_PRICE)
                  }
                  disabled={
                    selected.type === 'Personalizado' &&
                    (!selected.value || selected.value < MIN_PRICE)
                  }
                  to={{
                    pathname: '/vender-imovel/',
                    state: {
                      price: selected.value,
                      ...resultSetup.listing
                    }
                  }}
                  onClick={() => {
                    logger.action('seller-evaluation-sell-cta')
                    logger.action('seller-evaluation-sell-send', {
                      price: selected.value
                    })
                  }}
                >
                  <Text
                    color={
                      selected.type !== 'Personalizado' ||
                      (selected.type === 'Personalizado' &&
                        !!selected.value &&
                        selected.value >= MIN_PRICE)
                        ? '#FFFFFF'
                        : ''
                    }
                    size="16px"
                    weight="500"
                  >
                    Começar a vender
                  </Text>
                </Button>
              </Row>
              <BottomDiv
                margin="15px 0px"
                px={4}
                py={5}
                backgroundColor={'#E0E6ED'}
                style={{borderRadius: '4px'}}
              >
                <Row margin="10px" flexDirection="column">
                  <Text size="16px" weight="500">
                    Procurando imóvel?
                  </Text>
                  <Text size="14px">
                    Conte o que você está procurando para ver imóveis que
                    combinam com o seu perfil e receber novidades via email e
                    WhatsApp
                  </Text>
                </Row>
                <Button
                  width="20em"
                  to={idealForYouUrl}
                  onClick={() => {
                    const {
                      maxPrice,
                      minPrice,
                      listing: {types, addressData},
                      info: {area}
                    } = resultSetup

                    const address = getAddressInput({addressData})
                    const params = {
                      city: slugify(address.city).toLowerCase(),
                      state: slugify(address.state).toLowerCase()
                    }
                    const filters = {
                      price: {
                        min: calcPercentage(minPrice, SIMILARMIN),
                        max: calcPercentage(maxPrice, SIMILARMAX)
                      },
                      types,
                      area: {
                        min: calcPercentage(area, SIMILARMIN),
                        max: calcPercentage(area, SIMILARMAX)
                      }
                    }

                    logger.action('seller-evaluation-search-cta', {
                      filters,
                      ...params
                    })
                  }}
                >
                  Ver recomendações
                </Button>
              </BottomDiv>
            </Row>
          </Row>
        </Container>
      )}
    </>
  )
}

Evaluation.propTypes = {
  resultSetup: PropTypes.shape({
    success: PropTypes.bool.isRequired,
    listing: PropTypes.shape({
      address: PropTypes.string.isRequired,
      types: PropTypes.arrayOf(PropTypes.string).isRequired,
      streetNumber: PropTypes.string.isRequired,
      streetNumberNotFound: PropTypes.bool.isRequired,
      addressData: PropTypes.object.isRequired,
      complement: PropTypes.string
    }).isRequired,
    info: PropTypes.shape({
      area: PropTypes.number.isRequired
    }).isRequired,
    estimatedPrice: PropTypes.number.isRequired,
    maxPrice: PropTypes.number.isRequired,
    minPrice: PropTypes.number.isRequired
  }).isRequired
}

export default Evaluation
